import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';

/**
 * SEO Component for dynamic meta tags per page
 * Uses react-helmet-async to inject meta tags into the document head
 * 
 * @param {Object} props - SEO properties
 * @param {string} props.title - Page title (will be appended with " | AGNIR")
 * @param {string} props.description - Meta description for the page
 * @param {string} props.keywords - Comma-separated keywords for the page
 * @param {string} props.image - OG/Twitter image URL (defaults to hero image)
 * @param {string} props.url - Canonical URL for the page
 * @param {string} props.type - OG type (website, product, article, etc.)
 * @param {Object} props.product - Product data for product pages (structured data)
 * @param {Object} props.breadcrumbs - Breadcrumb data for structured data
 */
const SEO = ({ 
  title = 'Premium Handcrafted Jewelry',
  description = "Discover AGNIR's exquisite collection of handcrafted jewelry. Shop premium rings, bracelets, chains, and pendants with fire-inspired aesthetics.",
  keywords = 'AGNIR, jewelry, premium jewelry, handcrafted jewelry, rings, bracelets, chains, pendants, fashion jewelry, fire aesthetics',
  image = 'https://agnir.co/hero-bg.webp',
  url = 'https://agnir.co/',
  type = 'website',
  product = null,
  breadcrumbs = null,
  noIndex = false
}) => {
  const fullTitle = title === 'Home' 
    ? 'AGNIR - Premium Handcrafted Jewelry | Rings, Bracelets, Chains & Pendants'
    : `${title} | AGNIR`;
  
  const baseKeywords = 'AGNIR, jewelry, premium jewelry, handcrafted jewelry, fire aesthetics, Agnir fire, luxury fashion, elegant accessories';
  const fullKeywords = keywords ? `${keywords}, ${baseKeywords}` : baseKeywords;

  // Generate Product structured data
  const getProductSchema = () => {
    if (!product) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "description": product.description,
      "image": product.images || [product.image],
      "brand": {
        "@type": "Brand",
        "name": "AGNIR"
      },
      "sku": product.sku || product.id,
      "offers": {
        "@type": "Offer",
        "url": url,
        "priceCurrency": "INR",
        "price": product.price,
        "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        "availability": product.inStock !== false 
          ? "https://schema.org/InStock" 
          : "https://schema.org/OutOfStock",
        "seller": {
          "@type": "Organization",
          "name": "AGNIR"
        }
      },
      ...(product.rating && {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": product.rating,
          "reviewCount": product.reviewCount || 1
        }
      }),
      "category": product.category
    };
  };

  // Generate Breadcrumb structured data
  const getBreadcrumbSchema = () => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url
      }))
    };
  };

  const productSchema = getProductSchema();
  const breadcrumbSchema = getBreadcrumbSchema();

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={fullKeywords} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={product ? 'product' : type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="AGNIR" />
      <meta property="og:locale" content="en_US" />
      
      {/* Product-specific OG tags */}
      {product && (
        <>
          <meta property="product:price:amount" content={product.price} />
          <meta property="product:price:currency" content="INR" />
          <meta property="product:availability" content={product.inStock !== false ? 'in stock' : 'out of stock'} />
          <meta property="product:brand" content="AGNIR" />
          {product.category && <meta property="product:category" content={product.category} />}
        </>
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Product Structured Data */}
      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}
      
      {/* Breadcrumb Structured Data */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  keywords: PropTypes.string,
  image: PropTypes.string,
  url: PropTypes.string,
  type: PropTypes.string,
  product: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.string),
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sku: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    inStock: PropTypes.bool,
    rating: PropTypes.number,
    reviewCount: PropTypes.number,
    category: PropTypes.string
  }),
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired
  })),
  noIndex: PropTypes.bool
};

export default SEO;
